import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Navigation from '../../components/Navigation/Navigation';
import { fetchProjectReports, fetchProjectProgress, fetchProjectPhotos, fetchLatestReportHtml, fetchReportHtmlById, fetchLatestReportPdfBlob, fetchReportPdfBlobById, getLatestReportHtmlUrl } from '../../api/stackersApi';
import './StackerProjectPage.scss';

function StackerProjectPage() {
  const { projectName, reportId: reportIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '';

  const decodedName = projectName ? decodeURIComponent(projectName) : '';
  const basePath = `/stacker-project/${encodeURIComponent(decodedName)}`;

  // Derive view from URL so browser back/forward works
  const reportId = reportIdParam ? parseInt(reportIdParam, 10) : null;
  let view = 'menu';
  if (reportId && !isNaN(reportId)) view = 'report';
  else if (pathname.endsWith('/daily')) view = 'daily';
  else if (pathname.endsWith('/progress')) view = 'progress';
  else if (pathname.endsWith('/photos')) view = 'photos';
  else if (pathname.includes('/reports')) view = 'reports';

  const [reports, setReports] = useState([]);
  const [progress, setProgress] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoUnitFilter, setPhotoUnitFilter] = useState('all');
  const [progressUnitFilter, setProgressUnitFilter] = useState('all');
  const [progressMinPct, setProgressMinPct] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dailyReportHtml, setDailyReportHtml] = useState(null);
  const [dailyReportLoading, setDailyReportLoading] = useState(false);
  const [dailyReportError, setDailyReportError] = useState(null);
  const [dailyPdfDownloading, setDailyPdfDownloading] = useState(false);
  const [dailyPdfError, setDailyPdfError] = useState(null);
  const [reportViewHtml, setReportViewHtml] = useState(null);
  const [reportViewLoading, setReportViewLoading] = useState(false);
  const [reportViewError, setReportViewError] = useState(null);
  const [reportPdfDownloading, setReportPdfDownloading] = useState(false);
  const [reportPdfError, setReportPdfError] = useState(null);
  const token = sessionStorage.getItem('token');

  // Fetch latest daily report when on /daily
  useEffect(() => {
    if (view !== 'daily' || !decodedName) return;
    let cancelled = false;
    setDailyReportError(null);
    setDailyPdfError(null);
    setDailyReportHtml(null);
    setDailyReportLoading(true);
    fetchLatestReportHtml(decodedName, token)
      .then(({ html }) => {
        if (!cancelled) {
          setDailyReportHtml(html);
          setDailyReportError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setDailyReportError(e.message || 'Failed to load daily report');
          setDailyReportHtml(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDailyReportLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, decodedName, token]);

  // Fetch progress when on /progress
  useEffect(() => {
    if (view !== 'progress' || !decodedName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProgressUnitFilter('all');
    setProgressMinPct('0');
    fetchProjectProgress(decodedName, token)
      .then((data) => {
        if (!cancelled) setProgress(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load progress');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, decodedName, token]);

  // Fetch photos when on /photos
  useEffect(() => {
    if (view !== 'photos' || !decodedName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPhotoUnitFilter('all');
    fetchProjectPhotos(decodedName, token)
      .then((list) => {
        if (cancelled) return;
        // Oldest first; YYYY-MM-DD strings compare correctly as strings
        const sorted = [...list].sort((a, b) => {
          const da = a.reportDate || '';
          const db = b.reportDate || '';
          if (da !== db) return da < db ? -1 : 1;
          if (a.reportId !== b.reportId) return (a.reportId || 0) - (b.reportId || 0);
          return (a.id || 0) - (b.id || 0);
        });
        setPhotos(sorted);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load photos');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, decodedName, token]);

  // Fetch reports list when on /reports (and not viewing a single report)
  useEffect(() => {
    if (view !== 'reports' || !decodedName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProjectReports(decodedName, token)
      .then((list) => {
        if (!cancelled) setReports(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load reports');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, decodedName, token]);

  // Fetch single report HTML when on /reports/:reportId
  useEffect(() => {
    if (view !== 'report' || !decodedName || !reportId) return;
    let cancelled = false;
    setReportViewError(null);
    setReportPdfError(null);
    setReportViewHtml(null);
    setReportViewLoading(true);
    fetchReportHtmlById(decodedName, reportId, token)
      .then(({ html }) => {
        if (!cancelled) {
          setReportViewHtml(html);
          setReportViewError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setReportViewError(e.message || 'Failed to load report');
          setReportViewHtml(null);
        }
      })
      .finally(() => {
        if (!cancelled) setReportViewLoading(false);
      });
    return () => { cancelled = true; };
  }, [view, decodedName, reportId, token]);

  const handleDailyReport = () => navigate(`${basePath}/daily`);
  const handleProgressByUnit = () => navigate(`${basePath}/progress`);
  const handleAllReports = () => navigate(`${basePath}/reports`);
  const handlePhotos = () => navigate(`${basePath}/photos`);
  const handleOpenReport = (id) => navigate(`${basePath}/reports/${id}`);
  const handleBackToOptions = () => navigate(basePath);
  const handleBackToList = () => navigate(`${basePath}/reports`);

  const handleDownloadLatestPdf = async () => {
    if (!decodedName || dailyPdfDownloading) return;
    try {
      setDailyPdfDownloading(true);
      setDailyPdfError(null);
      const blob = await fetchLatestReportPdfBlob(decodedName, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeProject = String(decodedName || 'project').replace(/[^a-zA-Z0-9_-]+/g, '_');
      const datePart = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${safeProject}-daily-report-${datePart}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.message || 'Failed to download PDF';
      const missingPdfRoute = msg.includes('Cannot GET') || msg.includes('/reports/latest/pdf') || msg.includes('404');
      if (missingPdfRoute) {
        const htmlUrl = getLatestReportHtmlUrl(decodedName);
        window.open(htmlUrl, '_blank', 'noopener,noreferrer');
        setDailyPdfError('PDF endpoint is not available on the current server yet. Opened the report in a new tab - use Print > Save as PDF.');
      } else {
        setDailyPdfError(msg);
      }
    } finally {
      setDailyPdfDownloading(false);
    }
  };

  const handleDownloadReportPdfById = async () => {
    if (!decodedName || !reportId || reportPdfDownloading) return;
    try {
      setReportPdfDownloading(true);
      setReportPdfError(null);
      const blob = await fetchReportPdfBlobById(decodedName, reportId, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeProject = String(decodedName || 'project').replace(/[^a-zA-Z0-9_-]+/g, '_');
      const datePart = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${safeProject}-daily-report-${reportId}-${datePart}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setReportPdfError(e.message || 'Failed to download PDF');
    } finally {
      setReportPdfDownloading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // reportDate is a plain YYYY-MM-DD string; build the Date from parts so the
  // displayed day doesn't shift a day back in timezones behind UTC
  const formatDateOnly = (s) => {
    if (!s) return 'Unknown date';
    const [y, m, d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return String(s);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Sort unit names numerically (Unit 1, Unit 2, … Unit 24)
  const sortUnitNames = (names) => {
    const unitNum = (name) => {
      const m = String(name).match(/(\d+)/);
      return m ? parseInt(m[1], 10) : null;
    };
    return [...names].sort((a, b) => {
      const na = unitNum(a);
      const nb = unitNum(b);
      if (na !== null && nb !== null) return na - nb;
      if (na !== null) return -1;
      if (nb !== null) return 1;
      return String(a).localeCompare(String(b));
    });
  };

  // Unique unit names across all photos
  const photoUnitOptions = (() => {
    const seen = new Set();
    photos.forEach((p) => { if (p.unit) seen.add(p.unit); });
    return sortUnitNames(seen);
  })();

  const progressUnitOptions = (progress && progress.units)
    ? sortUnitNames(Object.keys(progress.units))
    : [];

  const renderPhotos = () => {
    if (!photos.length) return <p className="text-muted">No photos uploaded yet.</p>;
    const visiblePhotos = photoUnitFilter === 'all'
      ? photos
      : photos.filter((p) => p.unit === photoUnitFilter);
    if (!visiblePhotos.length) return <p className="text-muted">No photos for {photoUnitFilter}.</p>;
    const dateKeys = [];
    const byDate = new Map();
    visiblePhotos.forEach((p) => {
      const key = p.reportDate || 'unknown';
      if (!byDate.has(key)) {
        byDate.set(key, []);
        dateKeys.push(key);
      }
      byDate.get(key).push(p);
    });
    return (
      <div className="stacker-photos">
        {dateKeys.map((dateKey) => (
          <div key={dateKey} className="stacker-photos__group">
            <h6 className="stacker-photos__date">
              {dateKey === 'unknown' ? 'Unknown date' : formatDateOnly(dateKey)}
              <span className="stacker-photos__count"> — {byDate.get(dateKey).length} photo{byDate.get(dateKey).length === 1 ? '' : 's'}</span>
            </h6>
            <div className="stacker-photos__grid">
              {byDate.get(dateKey).map((p) => (
                <div key={p.id} className="stacker-photo-card">
                  <a href={p.imageUrl} target="_blank" rel="noopener noreferrer" title="Open full-size photo">
                    <img
                      src={p.thumbnailUrl || p.imageUrl}
                      alt={p.unit ? `${p.unit} site photo` : 'Site photo'}
                      loading="lazy"
                    />
                  </a>
                  <div className="stacker-photo-card__meta">
                    <span className="stacker-photo-card__unit">{p.unit || 'No unit'}</span>
                    <button
                      type="button"
                      className="stacker-photo-card__report"
                      onClick={() => handleOpenReport(p.reportId)}
                      title="Open the report this photo belongs to"
                    >
                      Report #{p.reportId}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const computeUnitPct = (unitData) => {
    // Prefer the server's weighted % (same math as the project card graph).
    if (unitData && typeof unitData.completionPercent === 'number') return unitData.completionPercent;
    let unitDone = 0;
    let unitTotal = 0;
    if (unitData && unitData.checkboxes) {
      Object.values(unitData.checkboxes).forEach((arr) => {
        unitTotal += Array.isArray(arr) ? arr.length : 0;
        unitDone += Array.isArray(arr) ? arr.filter(Boolean).length : 0;
      });
    }
    return unitTotal ? Math.round((unitDone / unitTotal) * 100) : 0;
  };

  const renderProgressByUnit = () => {
    if (!progress || !progress.units) return null;
    const units = progress.units;
    const entries = Object.entries(units)
      .map(([unitName, unitData]) => ({ unitName, unitData, unitPct: computeUnitPct(unitData) }))
      .filter(({ unitName, unitPct }) => {
        if (progressUnitFilter !== 'all' && unitName !== progressUnitFilter) return false;
        if (unitPct < Number(progressMinPct)) return false;
        return true;
      });
    if (!entries.length) {
      return <p className="text-muted">No units match the selected filters.</p>;
    }
    return (
      <div className="stacker-progress-by-unit">
        {entries.map(({ unitName, unitData, unitPct }) => {
          return (
            <div key={unitName} className="unit-block">
              <div className="unit-title">
                {unitName}
                <span className="unit-title__pct"> ({unitPct}%)</span>
              </div>
              {unitData.checkboxes && Object.entries(unitData.checkboxes).map(([stepName, arr]) => {
                const total = Array.isArray(arr) ? arr.length : 0;
                const done = Array.isArray(arr) ? arr.filter(Boolean).length : 0;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={stepName} className="step-row">
                    <span>{stepName}</span>
                    <span className={done >= total ? 'step-done' : 'step-pending'}>
                      {done}/{total} ({pct}%)
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Navigation />
      <div className="stacker-project-page">
        <div className="stacker-project-page__header">
          <Button variant="link" className="stacker-project-page__back" onClick={() => navigate('/welcome')}>
            ← Back to Home
          </Button>
          <h4 className="stacker-project-page__title">{decodedName} – Stacker Reports</h4>
        </div>

        {view === 'menu' && (
          <div className="cards d-flex justify-content-center flex-wrap gap-3">
            <button type="button" className="report-card__button stacker-option-card" onClick={handleDailyReport}>
              <Card className="m-2" style={{ width: '15rem' }}>
                <Card.Body>
                  <Card.Title>Daily Report</Card.Title>
                  <Card.Text className="text-muted small">Latest daily report (same as email)</Card.Text>
                </Card.Body>
              </Card>
            </button>
            <button type="button" className="report-card__button stacker-option-card" onClick={handleProgressByUnit}>
              <Card className="m-2" style={{ width: '15rem' }}>
                <Card.Body>
                  <Card.Title>Progress by unit</Card.Title>
                  <Card.Text className="text-muted small">What’s done and what’s not by unit</Card.Text>
                </Card.Body>
              </Card>
            </button>
            <button type="button" className="report-card__button stacker-option-card" onClick={handleAllReports}>
              <Card className="m-2" style={{ width: '15rem' }}>
                <Card.Body>
                  <Card.Title>All reports</Card.Title>
                  <Card.Text className="text-muted small">List of daily reports</Card.Text>
                </Card.Body>
              </Card>
            </button>
            <button type="button" className="report-card__button stacker-option-card" onClick={handlePhotos}>
              <Card className="m-2" style={{ width: '15rem' }}>
                <Card.Body>
                  <Card.Title>Photos</Card.Title>
                  <Card.Text className="text-muted small">Uploaded images by date, report, and unit</Card.Text>
                </Card.Body>
              </Card>
            </button>
          </div>
        )}

        {view === 'photos' && (
          <div className="stacker-content-view">
            <div className="stacker-content-view__toolbar">
              <Button variant="outline-secondary" size="sm" onClick={handleBackToOptions}>← Back to options</Button>
              {!loading && !error && photoUnitOptions.length > 0 && (
                <select
                  className="form-select form-select-sm stacker-photos__unit-filter"
                  value={photoUnitFilter}
                  onChange={(e) => setPhotoUnitFilter(e.target.value)}
                  aria-label="Filter photos by unit"
                >
                  <option value="all">All units</option>
                  {photoUnitOptions.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              )}
            </div>
            {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
            {error && <div className="alert alert-warning">{error}</div>}
            {!loading && !error && renderPhotos()}
          </div>
        )}

        {view === 'daily' && (
          <div className="stacker-daily-report-view">
            <div className="stacker-daily-report-view__toolbar">
              <Button variant="outline-secondary" size="sm" onClick={handleBackToOptions}>← Back to options</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDownloadLatestPdf}
                disabled={dailyReportLoading || !!dailyReportError || !dailyReportHtml || dailyPdfDownloading}
              >
                {dailyPdfDownloading ? 'Downloading PDF...' : 'Download PDF'}
              </Button>
            </div>
            {dailyReportLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" /> Loading report…
              </div>
            )}
            {dailyReportError && (
              <div className="alert alert-warning">
                {dailyReportError}
              </div>
            )}
            {dailyPdfError && !dailyReportError && (
              <div className="alert alert-warning">
                {dailyPdfError}
              </div>
            )}
            {!dailyReportLoading && !dailyReportError && dailyReportHtml && (
              <iframe
                title="Latest Daily Report"
                srcDoc={dailyReportHtml}
                className="stacker-daily-report-iframe"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        )}

        {view === 'progress' && (
          <div className="stacker-content-view">
            <div className="stacker-content-view__toolbar">
              <Button variant="outline-secondary" size="sm" onClick={handleBackToOptions}>← Back to options</Button>
              {!loading && !error && progressUnitOptions.length > 0 && (
                <>
                  <select
                    className="form-select form-select-sm stacker-toolbar-select"
                    value={progressUnitFilter}
                    onChange={(e) => setProgressUnitFilter(e.target.value)}
                    aria-label="Filter progress by unit"
                  >
                    <option value="all">All units</option>
                    {progressUnitOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm stacker-toolbar-select"
                    value={progressMinPct}
                    onChange={(e) => setProgressMinPct(e.target.value)}
                    aria-label="Filter progress by minimum completion percentage"
                  >
                    <option value="0">0% or more</option>
                    <option value="25">25% or more</option>
                    <option value="50">50% or more</option>
                    <option value="75">75% or more</option>
                    <option value="100">100%</option>
                  </select>
                </>
              )}
            </div>
            {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
            {error && <div className="alert alert-warning">{error}</div>}
            {!loading && !error && (progress && progress.exists ? renderProgressByUnit() : <p className="text-muted">No progress data.</p>)}
          </div>
        )}

        {view === 'reports' && (
          <div className="stacker-content-view">
            <div className="stacker-content-view__toolbar">
              <Button variant="outline-secondary" size="sm" onClick={handleBackToOptions}>← Back to options</Button>
            </div>
            {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
            {error && <div className="alert alert-warning">{error}</div>}
            {!loading && !error && (
              <div className="stacker-reports-list">
                <h6 className="stacker-reports-list__title">Daily reports — click a row to open</h6>
                {reports.length === 0 ? (
                  <p className="text-muted mb-0">No reports yet.</p>
                ) : (
                  reports.map((r) => (
                    <button
                      type="button"
                      key={r.id}
                      className="stacker-report-row"
                      onClick={() => handleOpenReport(r.id)}
                    >
                      <span className="stacker-report-row__date">{formatDate(r.report_date)}</span>
                      <span className="stacker-report-row__by">{r.submitted_by || '—'}</span>
                      <span className="stacker-report-row__summary">
                        {r.progress_summary ? (r.progress_summary.slice(0, 120) + (r.progress_summary.length > 120 ? '…' : '')) : '—'}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {view === 'report' && (
          <div className="stacker-daily-report-view">
            <div className="stacker-daily-report-view__toolbar">
              <Button variant="outline-secondary" size="sm" onClick={handleBackToList}>← Back to list</Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleDownloadReportPdfById}
                disabled={reportViewLoading || !!reportViewError || !reportViewHtml || reportPdfDownloading}
              >
                {reportPdfDownloading ? 'Downloading PDF...' : 'Download PDF'}
              </Button>
            </div>
            {reportViewLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" /> Loading report…
              </div>
            )}
            {reportViewError && (
              <div className="alert alert-warning">{reportViewError}</div>
            )}
            {reportPdfError && !reportViewError && (
              <div className="alert alert-warning">{reportPdfError}</div>
            )}
            {!reportViewLoading && !reportViewError && reportViewHtml && (
              <iframe
                title="Daily Report"
                srcDoc={reportViewHtml}
                className="stacker-daily-report-iframe"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default StackerProjectPage;
